import { useState } from "react";

import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import usePlatform from "@/hooks/usePlatform";
import { alpha, useTheme } from "@mui/material/styles";
import { siteName, ROUTE_NUMBER_COUNT } from "@/constants/config";
import { SHOW_LOYALTY_PROGRAM, SHOW_PANORAMA_PROGRAM } from "@/constants/programs";
import ThumbUpIcon from '@mui/icons-material/ThumbUp';

const Highlights = () => {
  const theme = useTheme();
  // join loyalty program with coma
  const [searchVisible, setSearchVisible] = useState(false);
  const [routeVisible, setRouteVisible] = useState(false);
  const loyaltyProgram = SHOW_LOYALTY_PROGRAM.join(", ");
  const { isPc } = usePlatform();
  const routeLoyalProgram = SHOW_PANORAMA_PROGRAM.join(", ");
  return (
    <Box className="my-[30px] !mt-[30px] ]">
      <Typography
        className="flex items-center !mb-[10px]"
        color="textPrimary"
        fontWeight={20000}
        component={"div"}
      >
        <div className="flex md:mx-[5px] mx-[15px] flex-wrap items-center text-[16px] md:text-[20px] mb-[10px]">
          <ThumbUpIcon color="primary" sx={{ mr: isPc ? 1 : 0.5 }} />
          Award Travel: Effortlessly Planned
        </div>
      </Typography>
      <Grid container spacing={2}>
        {[
          {
            title: "Real-time Search",
            subtitle: (
              <Typography component={"div"}>
                Free roundtrip, multicity and multiday award flight search for{" "}
                <Tooltip title={loyaltyProgram} placement="top">
                  <Typography
                    onClick={() => {
                      setSearchVisible(true);
                    }}
                    className="inline underline"
                  >
                    {SHOW_LOYALTY_PROGRAM.length} major airline loyalty programs
                  </Typography>
                </Tooltip>
                . Search is done under 20 seconds on average.
              </Typography>
            ),
            icon: (
              <svg
                height={24}
                width={24}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                />
              </svg>
            ),
          },
          {
            title: ROUTE_NUMBER_COUNT.toLocaleString() + "+ Routes",
            subtitle: (
              <Typography component={"div"}>
                {ROUTE_NUMBER_COUNT.toLocaleString() + "+ routes from "}
                <Tooltip title={routeLoyalProgram} placement="top">
                  <Typography
                    onClick={() => {
                      setRouteVisible(true);
                    }}
                    className="inline underline"
                  >
                    {SHOW_PANORAMA_PROGRAM.length} major airlines loyalty programs
                  </Typography>
                </Tooltip>{" "}
                are tracked over 24-48 hours for you to access year-around data at lighting fast
                speed.
              </Typography>
            ),
            icon: (
              <svg
                height={24}
                width={24}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                />
              </svg>
            ),
          },
          {
            title: siteName + " Award Alerts",
            subtitle:
              "Your ideal flight data delivered to your mailbox everyday. Enjoy the convenience of award alerts and never miss the flight.",
            icon: (
              <svg
                height={24}
                width={24}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                />
              </svg>
            ),
          },
        ].map((item, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Box width={1} height={"100%"} data-aos={"fade-up"}>
              <Box display={"flex"} flexDirection={"column"} alignItems={"center"}>
                <Box
                  component={Avatar}
                  width={60}
                  height={60}
                  marginBottom={2}
                  bgcolor={alpha(theme.palette.primary.main, 0.1)}
                  color={theme.palette.primary.main}
                >
                  {item.icon}
                </Box>
                <Typography
                  variant={"h6"}
                  gutterBottom
                  sx={{ fontWeight: 500 }}
                  align={"center"}
                  color="textPrimary"
                >
                  {item.title}
                </Typography>
                <Typography align={"center"} color="textSecondary" component={"div"}>
                  {item.subtitle}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Dialog
        open={searchVisible}
        onClose={() => setSearchVisible(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogContent>
          <Typography fontSize={16}>{loyaltyProgram}</Typography>
        </DialogContent>
      </Dialog>
      <Dialog
        open={routeVisible}
        onClose={() => setRouteVisible(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogContent>
          <Typography fontSize={16}>{routeLoyalProgram}</Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Highlights;
